import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import { centralMonth } from '../utils/centralDate';

type ReportRow = Record<string, string | number | null | undefined>;

type AbcPayload = {
  report: string;
  title: string;
  ekos: string;
  note?: string;
  startDate?: string;
  endDate?: string;
  asOfDate?: string;
  site: string;
  count: number;
  summary?: ReportRow[];
  rows?: ReportRow[];
  grouped?: ReportRow[];
  summaryColumns?: string[];
  detailColumns?: string[];
  allocation?: ReportRow[];
  allocationColumns?: string[];
  allocationRule?: string;
};

type ReportDef = {
  id: string;
  title: string;
  api: string;
  mode: 'range' | 'asof';
  taproomToggle?: boolean;
};

const REPORTS: Record<string, ReportDef> = {
  'taproom-sales': {
    id: 'taproom-sales',
    title: 'Taproom sales',
    api: '/api/reports/abc/taproom-sales',
    mode: 'range',
  },
  'distributor-invoices': {
    id: 'distributor-invoices',
    title: 'Distributor invoices',
    api: '/api/reports/abc/distributor-invoices',
    mode: 'range',
  },
  transfers: {
    id: 'transfers',
    title: 'Beer transfers',
    api: '/api/reports/abc/transfers',
    mode: 'range',
    taproomToggle: true,
  },
  packaging: {
    id: 'packaging',
    title: 'Batch packaging',
    api: '/api/reports/abc/packaging',
    mode: 'range',
  },
  'inventory-as-of': {
    id: 'inventory-as-of',
    title: 'Inventory as of date',
    api: '/api/reports/abc/inventory-as-of',
    mode: 'asof',
  },
};

const COLUMN_LABELS: Record<string, string> = {
  company: 'Company',
  site: 'Site',
  product: 'Product',
  cases: 'Cases',
  bblSixth: '1/6 BBL',
  bblQuarter: '1/4 BBL',
  bblHalf: '1/2 BBL',
  otherQty: 'Other',
  invoiceDate: 'Invoice date',
  invoiceNumber: 'Invoice #',
  customer: 'Customer',
  itemName: 'Item',
  packageType: 'Package type',
  packageGroup: 'Package group',
  quantity: 'Qty',
  unitPrice: 'Unit price',
  extended: 'Extended',
  transferDate: 'Date',
  transferNumber: 'Transfer #',
  toNumber: 'TO #',
  fromSite: 'From site',
  fromLocation: 'From location',
  toSite: 'To site',
  toLocation: 'To location',
  origin: 'Origin',
  date: 'Date',
  batchId: 'Batch',
  volumeBbl: 'Volume (bbl)',
  source: 'Source',
  location: 'Location',
  itemCount: 'Items',
};

function pad(n: number) {
  return String(n).padStart(2, '0');
}

// Months are America/Chicago months (the ABC return month), not the browser's or UTC.
function previousMonth(): string {
  const [y, m] = centralMonth().split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 2, 1));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}`;
}

function firstOfThisMonth(): string {
  return `${centralMonth()}-01`;
}

function monthBounds(ym: string): { startDate: string; endDate: string } {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { startDate: `${ym}-01`, endDate: `${ym}-${pad(last)}` };
}

function formatCell(value: string | number | null | undefined): string {
  if (value == null || value === '') return '';
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : String(Math.round(value * 1000) / 1000);
  }
  return String(value);
}

function ReportTable({ columns, rows, testId }: { columns: string[]; rows: ReportRow[]; testId: string }) {
  if (!rows.length) {
    return <p data-testid={`${testId}-empty`}>No rows for this period.</p>;
  }
  return (
    <div className="ttb-report__table-wrap">
      <table className="ttb-report__table inventory-table" data-testid={testId}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col}>{COLUMN_LABELS[col] || col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td key={col} className={typeof row[col] === 'number' ? 'ttb-report__num' : undefined}>
                  {formatCell(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const AbcBeerReport: React.FC = () => {
  const { reportId = '' } = useParams();
  const def = REPORTS[reportId];
  const [month, setMonth] = useState(previousMonth);
  const [fromDate, setFromDate] = useState(() => monthBounds(previousMonth()).startDate);
  const [toDate, setToDate] = useState(() => monthBounds(previousMonth()).endDate);
  const [asOfDate, setAsOfDate] = useState(firstOfThisMonth);
  const [site, setSite] = useState('both');
  const [includeTaproomOrders, setIncludeTaproomOrders] = useState(false);
  const [report, setReport] = useState<AbcPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queryString = useMemo(() => {
    if (!def) return '';
    const qs = new URLSearchParams();
    qs.set('site', site);
    if (def.mode === 'asof') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(asOfDate)) return '';
      qs.set('asOfDate', asOfDate);
    } else {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(fromDate) || !/^\d{4}-\d{2}-\d{2}$/.test(toDate)) return '';
      qs.set('from', fromDate);
      qs.set('to', toDate);
    }
    if (def.taproomToggle) qs.set('includeTaproomOrders', includeTaproomOrders ? '1' : '0');
    return qs.toString();
  }, [def, site, asOfDate, fromDate, toDate, includeTaproomOrders]);

  const applyMonth = (ym: string) => {
    setMonth(ym);
    if (!/^\d{4}-\d{2}$/.test(ym)) return;
    const bounds = monthBounds(ym);
    setFromDate(bounds.startDate);
    setToDate(bounds.endDate);
  };

  const load = useCallback(async () => {
    if (!def || !queryString) return;
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${def.api}?${queryString}`);
      const body = (await res.json()) as AbcPayload;
      setReport(body);
    } catch (err: unknown) {
      setReport(null);
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [def, queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  const download = async (format: 'csv' | 'csv-detail') => {
    if (!def) return;
    try {
      const res = await apiFetch(`${def.api}?${queryString}&format=${format}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${def.id}${format === 'csv-detail' ? '-detail' : ''}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  if (!def) {
    return (
      <div data-testid="abc-report-missing">
        <p>
          <Link to="/reporting">All reports</Link>
        </p>
        <p>That report is not available.</p>
      </div>
    );
  }

  const summary = report?.summary || report?.grouped || [];
  const details = report?.rows || [];
  const summaryColumns = report?.summaryColumns || [];
  const detailColumns = report?.detailColumns || [];
  const showDetail =
    detailColumns.length > 0 &&
    (summaryColumns.join('|') !== detailColumns.join('|') || def.id === 'inventory-as-of');

  return (
    <section className="ttb-report abc-report" data-testid={`abc-report-${def.id}`}>
      <p className="no-print">
        <Link to="/reporting">All reports</Link>
      </p>
      <h2 className="app-header">{report?.title || def.title}</h2>
      {report?.ekos && <p className="ttb-report__note">{report.ekos}</p>}
      {report?.note && <p className="ttb-report__note">{report.note}</p>}

      <div className="ttb-report__controls no-print">
        {def.mode === 'range' ? (
          <>
            <label>
              Month
              <input
                type="month"
                value={month}
                onChange={(e) => applyMonth(e.target.value)}
                data-testid="abc-month"
              />
            </label>
            <label>
              From
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                data-testid="abc-from"
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                data-testid="abc-to"
              />
            </label>
          </>
        ) : (
          <label>
            On hand as of
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              data-testid="abc-as-of"
            />
          </label>
        )}
        <label>
          Site
          <select value={site} onChange={(e) => setSite(e.target.value)} data-testid="abc-site">
            <option value="both">Madison and Athens</option>
            <option value="madison">Madison</option>
            <option value="athens">Athens</option>
          </select>
        </label>
        {def.taproomToggle && (
          <label className="abc-report__check">
            <input
              type="checkbox"
              checked={includeTaproomOrders}
              onChange={(e) => setIncludeTaproomOrders(e.target.checked)}
              data-testid="abc-include-taproom"
            />
            Include taproom-order transfers
          </label>
        )}
        <button type="button" onClick={() => void load()} disabled={loading} data-testid="abc-run">
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <button type="button" onClick={() => void download('csv')} disabled={!report} data-testid="abc-csv">
          Export CSV
        </button>
        {showDetail && (
          <button type="button" onClick={() => void download('csv-detail')} disabled={!report} data-testid="abc-csv-detail">
            Export detail CSV
          </button>
        )}
      </div>

      {error && (
        <p className="ttb-report__error" data-testid="abc-error">
          {error}
        </p>
      )}

      {report && def.mode === 'range' && (
        <p className="ttb-report__note">
          {report.startDate} through {report.endDate}
          {report.site === 'both' ? '' : ` · ${report.site === 'madison' ? 'Madison' : 'Athens'}`}
          {` · ${report.count} line${report.count === 1 ? '' : 's'}`}
        </p>
      )}
      {report && def.mode === 'asof' && (
        <p className="ttb-report__note">
          As of {report.asOfDate}
          {` · ${summary.length} packaging group${summary.length === 1 ? '' : 's'}`}
        </p>
      )}

      {report && report.allocation && report.allocation.length > 0 && (
        <>
          <h3 className="ttb-report__title">BMFG-4 allocation (taproom sales this period)</h3>
          {report.allocationRule && <p className="ttb-report__note">{report.allocationRule}</p>}
          <ReportTable
            columns={report.allocationColumns || ['site', 'cases', 'bblSixth', 'bblQuarter', 'bblHalf']}
            rows={report.allocation}
            testId="abc-allocation"
          />
        </>
      )}

      {report && (
        <>
          <h3 className="ttb-report__title">{showDetail ? 'By package' : 'Lines'}</h3>
          <ReportTable columns={summaryColumns} rows={summary} testId="abc-summary" />
          {showDetail && (
            <>
              <h3 className="ttb-report__title">Line detail</h3>
              <ReportTable columns={detailColumns} rows={details} testId="abc-detail" />
            </>
          )}
        </>
      )}
    </section>
  );
};

export default AbcBeerReport;
